/* =========================================================
   UCMPC — AJUSTES GLOBALES DE INTERFAZ — V24
   Reparación quirúrgica
   ========================================================= */

'use strict';


/* =========================================================
   1. HEADER — RECÁLCULO SEGURO AL AMPLIAR LA VENTANA
   Conserva el comportamiento aprobado de Horizon.
   ========================================================= */

(function () {
  const DESKTOP_MIN_WIDTH = 750;

  let previousWidth = window.innerWidth;
  let resizeTimer = null;

  function refreshHeaderOverflow() {
    const currentWidth = window.innerWidth;
    const viewportIsGrowing = currentWidth > previousWidth;

    previousWidth = currentWidth;

    /*
     * Solo intervenimos cuando la ventana está creciendo.
     * Al reducirla, Horizon gestiona normalmente
     * More / menú hamburguesa.
     */
    if (
      !viewportIsGrowing ||
      currentWidth < DESKTOP_MIN_WIDTH
    ) {
      return;
    }

    const header =
      document.querySelector('#header-component');

    const overflowList =
      header?.querySelector('overflow-list');

    if (!header || !overflowList) {
      return;
    }

    /*
     * Limpia un posible estado residual de drawer antes
     * de pedir una nueva medición del menú de escritorio.
     */
    if (header.dataset.menuStyle === 'drawer') {
      overflowList.removeAttribute('minimum-reached');
      header.dataset.menuStyle = 'menu';
    }

    requestAnimationFrame(function () {
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: false,
          detail: {}
        })
      );
    });
  }

  function handleResize() {
    window.clearTimeout(resizeTimer);

    resizeTimer = window.setTimeout(
      refreshHeaderOverflow,
      140
    );
  }

  if (
    window.customElements &&
    typeof window.customElements.whenDefined === 'function'
  ) {
    window.customElements
      .whenDefined('overflow-list')
      .then(function () {
        window.addEventListener(
          'resize',
          handleResize,
          { passive: true }
        );
      })
      .catch(function () {});
  }
})();


/* =========================================================
   2. CONTEXTO VISUAL POR RUTA
   Solo añade clases de presentación. No altera lógica.
   ========================================================= */

(function () {
  function normalizedPath() {
    return window.location.pathname.replace(/\/$/, '');
  }

  function applyPageContext() {
    const path = normalizedPath();

    document.body.classList.toggle(
      'ucmpc-collection-cuentos-de-vida',
      path === '/collections/cuentos-de-vida'
    );

    document.body.classList.toggle(
      'ucmpc-story-product',
      path.startsWith('/products/')
    );
  }

  function initPageContext() {
    if (!document.body) return;
    applyPageContext();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initPageContext,
      { once: true }
    );
  } else {
    initPageContext();
  }

  document.addEventListener(
    'shopify:section:load',
    initPageContext
  );
})();


/* =========================================================
   3. MICROANIMACIÓN + ESTRUCTURA EDITORIAL V4
   Fundación · Impacto · Historias que Fortalecen
   ========================================================= */

(function () {
  let activeFirstScrollCleanup = null;

  function createAmbientLayer(editorial) {
    const shell = editorial.parentElement;

    if (!shell) return;

    shell.classList.add('ucmpc-editorial-shell');

    if (
      shell.querySelector(':scope > .ucmpc-editorial-ambient')
    ) {
      return;
    }

    const ambient = document.createElement('div');
    ambient.className = 'ucmpc-editorial-ambient';
    ambient.setAttribute('aria-hidden', 'true');

    for (let i = 0; i < 5; i += 1) {
      const particle = document.createElement('span');
      particle.className = 'ucmpc-editorial-particle';
      ambient.appendChild(particle);
    }

    shell.appendChild(ambient);
  }

  function normalizeText(element) {
    return element.textContent
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function markStrengtheningFronts(editorial) {
    if (
      !editorial.classList.contains(
        'ucmpc-page-historias-que-fortalecen'
      )
    ) {
      return [];
    }

    const cards = [];

    editorial
      .querySelectorAll(':scope > p')
      .forEach(function (paragraph) {
        const text = normalizeText(paragraph);

        if (text.startsWith('el primero es')) {
          paragraph.classList.add(
            'ucmpc-front-card',
            'ucmpc-front-card--social'
          );
          cards.push(paragraph);
        }

        if (text.startsWith('el segundo es')) {
          paragraph.classList.add(
            'ucmpc-front-card',
            'ucmpc-front-card--organizations'
          );
          cards.push(paragraph);
        }

        if (
          text.includes(
            'esta línea estará disponible próximamente'
          )
        ) {
          paragraph.classList.add('ucmpc-coming-soon');
        }
      });

    return cards;
  }

  function wrapStrengtheningFronts(editorial, cards) {
    if (cards.length < 2) return;

    if (
      editorial.querySelector(':scope > .ucmpc-fronts-grid')
    ) {
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'ucmpc-fronts-grid';

    cards[0].before(grid);
    cards.forEach(function (card) {
      grid.appendChild(card);
    });
  }

  function setupImpactSteps(editorial) {
    if (
      !editorial.classList.contains('ucmpc-page-impacto')
    ) {
      return;
    }

    if (
      editorial.querySelector(':scope > .ucmpc-impact-grid')
    ) {
      return;
    }

    const paragraphs = Array.from(
      editorial.querySelectorAll(':scope > p')
    );

    const steps = paragraphs.slice(1, 3);

    if (steps.length < 2) return;

    steps[0].classList.add(
      'ucmpc-impact-step',
      'ucmpc-impact-step--1'
    );

    steps[1].classList.add(
      'ucmpc-impact-step',
      'ucmpc-impact-step--2'
    );

    const grid = document.createElement('div');
    grid.className = 'ucmpc-impact-grid';

    steps[0].before(grid);
    steps.forEach(function (step) {
      grid.appendChild(step);
    });
  }

  function applyEditorialStructure(editorial) {
    const strengtheningCards =
      markStrengtheningFronts(editorial);

    wrapStrengtheningFronts(
      editorial,
      strengtheningCards
    );

    setupImpactSteps(editorial);
  }

  function collectRevealTargets(editorial, firstOriginHeading) {
    const targets = [];

    const opening = editorial.querySelector(
      ':scope > p:first-child'
    );

    if (opening) {
      opening.dataset.ucmpcRevealType = 'major';
      targets.push(opening);
    }

    editorial
      .querySelectorAll(':scope > h2')
      .forEach(function (heading) {
        if (heading === firstOriginHeading) return;

        heading.dataset.ucmpcRevealType = 'major';
        targets.push(heading);
      });

    editorial
      .querySelectorAll(
        ':scope > p, :scope > .ucmpc-fronts-grid > p, :scope > .ucmpc-impact-grid > p'
      )
      .forEach(function (paragraph) {
        if (paragraph === opening) return;
        if (targets.includes(paragraph)) return;

        paragraph.dataset.ucmpcRevealType =
          paragraph.classList.contains('ucmpc-front-card') ||
          paragraph.classList.contains('ucmpc-impact-step') ||
          paragraph.classList.contains('ucmpc-coming-soon')
            ? 'major'
            : 'soft';

        targets.push(paragraph);
      });

    targets.forEach(function (element, index) {
      element.classList.add('ucmpc-reveal');
      element.style.setProperty(
        '--ucmpc-reveal-delay',
        `${Math.min(index, 4) * 35}ms`
      );
    });

    return targets;
  }

  function revealTargetsOnViewport(targets) {
    if (!targets.length) return;

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                if (!entry.isIntersecting) return;

                entry.target.classList.add('is-visible');
                observer.unobserve(entry.target);
              });
            },
            {
              threshold: 0.10,
              rootMargin: '0px 0px -4% 0px'
            }
          );

          targets.forEach(function (element) {
            observer.observe(element);
          });
        } else {
          targets.forEach(function (element) {
            element.classList.add('is-visible');
          });
        }
      });
    });
  }

  function setupFoundationFirstScroll(editorial, firstHeading) {
    if (!firstHeading) return;

    firstHeading.classList.add('ucmpc-reveal');
    firstHeading.dataset.ucmpcRevealType = 'major';

    if (typeof activeFirstScrollCleanup === 'function') {
      activeFirstScrollCleanup();
      activeFirstScrollCleanup = null;
    }

    editorial.classList.add('ucmpc-first-scroll-pending');

    let revealed = false;
    let touchStartY = null;
    let previousScrollY = window.scrollY;

    function cleanup() {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll);
    }

    function revealFirstHeading() {
      if (revealed) return;

      revealed = true;
      firstHeading.classList.add('is-visible');
      editorial.classList.add('ucmpc-first-scroll-revealed');
      editorial.classList.remove('ucmpc-first-scroll-pending');
      cleanup();

      if (activeFirstScrollCleanup === cleanup) {
        activeFirstScrollCleanup = null;
      }
    }

    function onWheel(event) {
      if (event.deltaY > 0) revealFirstHeading();
    }

    function onTouchStart(event) {
      touchStartY =
        event.touches?.[0]?.clientY ?? null;
    }

    function onTouchEnd(event) {
      if (touchStartY === null) return;

      const touchEndY =
        event.changedTouches?.[0]?.clientY ?? touchStartY;

      if (touchEndY < touchStartY - 8) {
        revealFirstHeading();
      }

      touchStartY = null;
    }

    function onKeyDown(event) {
      if (
        ['ArrowDown', 'PageDown', 'End', ' ']
          .includes(event.key)
      ) {
        revealFirstHeading();
      }
    }

    function onScroll() {
      const currentScrollY = window.scrollY;

      if (currentScrollY > previousScrollY + 1) {
        revealFirstHeading();
        return;
      }

      previousScrollY = currentScrollY;
    }

    activeFirstScrollCleanup = cleanup;

    if (window.scrollY > 2) {
      revealFirstHeading();
      return;
    }

    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function initUcmpcEditorialMotion() {
    const editorial = document.querySelector(
      '.ucmpc-editorial-page'
    );

    if (!editorial) return;

    if (editorial.dataset.ucmpcMotionInit === 'true') {
      return;
    }

    editorial.dataset.ucmpcMotionInit = 'true';

    createAmbientLayer(editorial);
    applyEditorialStructure(editorial);

    const isFoundation = editorial.classList.contains(
      'ucmpc-page-la-fundacion'
    );

    const firstOriginHeading = isFoundation
      ? editorial.querySelector(':scope > h2:first-of-type')
      : null;

    const targets = collectRevealTargets(
      editorial,
      firstOriginHeading
    );

    editorial.classList.add('ucmpc-motion-ready');

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReducedMotion) {
      targets.forEach(function (element) {
        element.classList.add('is-visible');
      });

      if (firstOriginHeading) {
        firstOriginHeading.classList.add('is-visible');
      }

      editorial.classList.add('ucmpc-first-scroll-revealed');
      editorial.classList.remove('ucmpc-first-scroll-pending');
      return;
    }

    revealTargetsOnViewport(targets);

    if (firstOriginHeading) {
      setupFoundationFirstScroll(
        editorial,
        firstOriginHeading
      );
    }
  }

  function resetAndInitEditorialMotion() {
    const editorial = document.querySelector(
      '.ucmpc-editorial-page'
    );

    if (editorial) {
      delete editorial.dataset.ucmpcMotionInit;
    }

    initUcmpcEditorialMotion();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initUcmpcEditorialMotion,
      { once: true }
    );
  } else {
    initUcmpcEditorialMotion();
  }

  document.addEventListener(
    'shopify:section:load',
    resetAndInitEditorialMotion
  );
})();


/* =========================================================
   4. CUENTOS PARA NIÑOS — NAVEGACIÓN EN UN SOLO CLIC
   Conserva el comportamiento ya aprobado del header.
   ========================================================= */

(function () {
  document.addEventListener(
    'click',
    function (event) {
      const link = event.target.closest(
        '#header-component a[href]'
      );

      if (!link) return;

      const url = new URL(
        link.href,
        window.location.origin
      );

      if (
        url.pathname.replace(/\/$/, '') !==
        '/pages/cuentos-para-ninos'
      ) {
        return;
      }

      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button > 0
      ) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      window.location.assign(link.href);
    },
    true
  );
})();


/* =========================================================
   FIN V5

   Eliminado deliberadamente:
   - traducción de textos nativos de Shopify mediante JS;
   - TreeWalker de microcopy;
   - MutationObserver global;
   - manipulación JS del zoom de producto;
   - cualquier intervención sobre lectores, ownership,
     carrito, checkout, Locksmith o Mi Biblioteca.
   ========================================================= */

/* =========================================================
   5. UCMPC V6 — DETALLES EDITORIALES
   Textos finales + iconografía flotante en franjas laterales.
   ========================================================= */

(function () {

  const ICONS = {
    book:
      '<svg viewBox="0 0 48 48" aria-hidden="true" fill="none" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M7 10.5c6.4-2.2 12.1-.8 17 2.6v22c-4.9-3.4-10.6-4.8-17-2.6z"/>' +
      '<path d="M41 10.5c-6.4-2.2-12.1-.8-17 2.6v22c4.9-3.4 10.6-4.8 17-2.6z"/>' +
      '<path d="M24 13.1v22.1"/>' +
      '</svg>',

    pencil:
      '<svg viewBox="0 0 48 48" aria-hidden="true" fill="none" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M11 37l3.2-10.2L31.8 9.2l7 7-17.6 17.6z"/>' +
      '<path d="M28.8 12.2l7 7"/>' +
      '<path d="M14.2 26.8l7 7"/>' +
      '<path d="M11 37l10.2-3.2"/>' +
      '</svg>',

    sparkle:
      '<svg viewBox="0 0 48 48" aria-hidden="true" fill="none" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M24 7c1.6 9.4 6.6 14.4 16 16-9.4 1.6-14.4 6.6-16 16-1.6-9.4-6.6-14.4-16-16 9.4-1.6 14.4-6.6 16-16z"/>' +
      '<path d="M38 7v8"/><path d="M34 11h8"/>' +
      '</svg>',

    leaf:
      '<svg viewBox="0 0 48 48" aria-hidden="true" fill="none" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M38 10C22 11 12 18 11 31c8 5 21 2 27-21z"/>' +
      '<path d="M12 36c6-10 13-16 24-23"/>' +
      '</svg>',

    link:
      '<svg viewBox="0 0 48 48" aria-hidden="true" fill="none" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M20.2 30.8l-3.7 3.7a7.4 7.4 0 01-10.5-10.5l6.2-6.2a7.4 7.4 0 0110.5 0"/>' +
      '<path d="M27.8 17.2l3.7-3.7A7.4 7.4 0 0142 24l-6.2 6.2a7.4 7.4 0 01-10.5 0"/>' +
      '<path d="M17.5 30.5l13-13"/>' +
      '</svg>',

    sprout:
      '<svg viewBox="0 0 48 48" aria-hidden="true" fill="none" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M24 39V23"/>' +
      '<path d="M24 27c-8 0-13-4-14-11 8-1 13 3 14 11z"/>' +
      '<path d="M24 22c1-8 6-12 14-11-1 7-6 11-14 11z"/>' +
      '</svg>',

    ripple:
      '<svg viewBox="0 0 48 48" aria-hidden="true" fill="none" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round">' +
      '<circle cx="24" cy="24" r="4"/>' +
      '<circle cx="24" cy="24" r="11"/>' +
      '<circle cx="24" cy="24" r="18"/>' +
      '</svg>',

    path:
      '<svg viewBox="0 0 48 48" aria-hidden="true" fill="none" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M9 36c7-17 11-8 17-19 4-7 8-8 13-5"/>' +
      '<path d="M34 8l5 4-4 5"/>' +
      '<circle cx="9" cy="36" r="2.2"/>' +
      '</svg>'
  };


  const PAGE_ICON_SETS = {
    foundation: [
      'book',
      'sparkle',
      'pencil',
      'leaf',
      'book',
      'path',
      'sparkle',
      'leaf'
    ],

    strengthening: [
      'book',
      'link',
      'sprout',
      'sparkle',
      'leaf',
      'link',
      'book',
      'sprout'
    ],

    impact: [
      'ripple',
      'sprout',
      'path',
      'sparkle',
      'leaf',
      'ripple',
      'path',
      'sprout'
    ]
  };


  function replaceCardCopy(card, lead, copy) {
    if (!card) return;

    const strong =
      document.createElement('strong');

    strong.textContent = lead;

    card.replaceChildren(
      strong,
      document.createTextNode(' ' + copy)
    );
  }


  function applyStrengtheningCopy(editorial) {
    if (
      !editorial.classList.contains(
        'ucmpc-page-historias-que-fortalecen'
      )
    ) {
      return;
    }

    const social =
      editorial.querySelector(
        '.ucmpc-front-card--social'
      );

    const organizations =
      editorial.querySelector(
        '.ucmpc-front-card--organizations'
      );

    replaceCardCopy(
      social,
      'Acci\u00F3n social:',
      'procesos gratuitos de acompa\u00F1amiento para personas con discapacidad f\u00EDsica, orientados a fortalecer seguridad interior, autoconfianza, lenguaje personal, lectura de la propia historia y estrategias de afrontamiento desde la identidad narrativa.'
    );

    replaceCardCopy(
      organizations,
      'Servicio institucional para empresas:',
      'acompa\u00F1amiento a empleados con discapacidad f\u00EDsica, dise\u00F1ado para fortalecer confianza, comunicaci\u00F3n, lectura del entorno laboral, permanencia y desarrollo de talento dentro de la organizaci\u00F3n.'
    );
  }


  function getPageType(editorial) {
    if (
      editorial.classList.contains(
        'ucmpc-page-la-fundacion'
      )
    ) {
      return 'foundation';
    }

    if (
      editorial.classList.contains(
        'ucmpc-page-historias-que-fortalecen'
      )
    ) {
      return 'strengthening';
    }

    if (
      editorial.classList.contains(
        'ucmpc-page-impacto'
      )
    ) {
      return 'impact';
    }

    return null;
  }


  function seededRandom(seed) {
    let value = seed >>> 0;

    return function () {
      value += 0x6D2B79F5;

      let t = value;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);

      return (
        ((t ^ (t >>> 14)) >>> 0) /
        4294967296
      );
    };
  }


  function pageSeed(pageType) {
    if (pageType === 'foundation') return 1729;
    if (pageType === 'strengthening') return 2843;
    return 3911;
  }


  function removeLegacyDecor() {
    document
      .querySelectorAll(
        '.ucmpc-editorial-side-decor'
      )
      .forEach(function (node) {
        node.remove();
      });
  }


  function createGutterDecor(editorial) {
    const pageType = getPageType(editorial);
    if (!pageType) return;

    const main =
      document.querySelector('#MainContent');

    if (!main) return;

    removeLegacyDecor();

    let decor =
      main.querySelector(
        ':scope > .ucmpc-editorial-gutter-decor'
      );

    if (!decor) {
      decor = document.createElement('div');
      decor.className =
        'ucmpc-editorial-gutter-decor';
      decor.setAttribute('aria-hidden', 'true');
      main.prepend(decor);
    }

    decor.replaceChildren();

    PAGE_ICON_SETS[pageType]
      .forEach(function (iconName, index) {
        const icon =
          document.createElement('span');

        icon.className =
          'ucmpc-editorial-gutter-icon';

        icon.dataset.icon = iconName;
        icon.dataset.index = String(index);
        icon.innerHTML = ICONS[iconName];

        decor.appendChild(icon);
      });

    layoutGutterDecor(
      editorial,
      decor,
      pageType
    );
  }


  function layoutGutterDecor(
    editorial,
    decor,
    pageType
  ) {
    if (
      window.innerWidth < 1180 ||
      !editorial ||
      !decor
    ) {
      if (decor) decor.hidden = true;
      return;
    }

    const main =
      document.querySelector('#MainContent');

    if (!main) return;

    const mainRect =
      main.getBoundingClientRect();

    const editorialRect =
      editorial.getBoundingClientRect();

    const iconSize = 40;
    const outerMargin = 24;
    const contentGap = 58;

    const centralLeft =
      editorialRect.left - mainRect.left;

    const centralRight =
      editorialRect.right - mainRect.left;

    const leftMin = outerMargin;
    const leftMax =
      centralLeft - contentGap - iconSize;

    const rightMin =
      centralRight + contentGap;

    const rightMax =
      mainRect.width - outerMargin - iconSize;

    if (
      leftMax <= leftMin ||
      rightMax <= rightMin
    ) {
      decor.hidden = true;
      return;
    }

    decor.hidden = false;

    const icons = Array.from(
      decor.querySelectorAll(
        '.ucmpc-editorial-gutter-icon'
      )
    );

    const rand =
      seededRandom(pageSeed(pageType));

    const mainHeight =
      Math.max(
        main.scrollHeight,
        editorial.offsetTop + editorial.offsetHeight
      );

    const topSafe = 110;
    const bottomSafe = 120;
    const availableHeight =
      Math.max(500, mainHeight - topSafe - bottomSafe);

    const leftIcons =
      icons.filter(function (_, index) {
        return index % 2 === 0;
      });

    const rightIcons =
      icons.filter(function (_, index) {
        return index % 2 === 1;
      });

    function placeColumn(columnIcons, side) {
      const count = columnIcons.length;
      const slotHeight = availableHeight / count;

      columnIcons.forEach(function (icon, slotIndex) {
        const xMin =
          side === 'left' ? leftMin : rightMin;

        const xMax =
          side === 'left' ? leftMax : rightMax;

        const x =
          xMin + rand() * (xMax - xMin);

        const yJitter =
          (rand() - 0.5) * slotHeight * 0.34;

        const y =
          topSafe +
          slotHeight * (slotIndex + 0.5) +
          yJitter;

        const size =
          34 + Math.round(rand() * 12);

        const rotate =
          -10 + rand() * 20;

        const driftX1 =
          (rand() - 0.5) * 18;

        const driftX2 =
          (rand() - 0.5) * 20;

        const driftX3 =
          (rand() - 0.5) * 16;

        const driftY1 =
          -6 - rand() * 10;

        const driftY2 =
          4 + rand() * 10;

        const driftY3 =
          -3 - rand() * 8;

        icon.style.setProperty(
          '--ucmpc-icon-x',
          x.toFixed(1) + 'px'
        );

        icon.style.setProperty(
          '--ucmpc-icon-y',
          y.toFixed(1) + 'px'
        );

        icon.style.setProperty(
          '--ucmpc-icon-size',
          size + 'px'
        );

        icon.style.setProperty(
          '--ucmpc-icon-rotate',
          rotate.toFixed(1) + 'deg'
        );

        icon.style.setProperty(
          '--ucmpc-icon-dx1',
          driftX1.toFixed(1) + 'px'
        );

        icon.style.setProperty(
          '--ucmpc-icon-dx2',
          driftX2.toFixed(1) + 'px'
        );

        icon.style.setProperty(
          '--ucmpc-icon-dx3',
          driftX3.toFixed(1) + 'px'
        );

        icon.style.setProperty(
          '--ucmpc-icon-dy1',
          driftY1.toFixed(1) + 'px'
        );

        icon.style.setProperty(
          '--ucmpc-icon-dy2',
          driftY2.toFixed(1) + 'px'
        );

        icon.style.setProperty(
          '--ucmpc-icon-dy3',
          driftY3.toFixed(1) + 'px'
        );

        icon.style.setProperty(
          '--ucmpc-icon-duration',
          (21 + rand() * 11).toFixed(1) + 's'
        );

        icon.style.setProperty(
          '--ucmpc-icon-delay',
          (-rand() * 12).toFixed(1) + 's'
        );

        icon.style.setProperty(
          '--ucmpc-icon-opacity',
          (0.072 + rand() * 0.035).toFixed(3)
        );
      });
    }

    placeColumn(leftIcons, 'left');
    placeColumn(rightIcons, 'right');
  }


  let resizeTimer = null;

  function scheduleDecorLayout() {
    window.clearTimeout(resizeTimer);

    resizeTimer = window.setTimeout(
      function () {
        const editorial =
          document.querySelector(
            '.ucmpc-editorial-page'
          );

        const decor =
          document.querySelector(
            '#MainContent > .ucmpc-editorial-gutter-decor'
          );

        if (!editorial || !decor) return;

        layoutGutterDecor(
          editorial,
          decor,
          getPageType(editorial)
        );
      },
      120
    );
  }


  function initV6EditorialDetails() {
    const editorial =
      document.querySelector(
        '.ucmpc-editorial-page'
      );

    if (!editorial) return;

    applyStrengtheningCopy(editorial);
    createGutterDecor(editorial);
  }


  function scheduleInit() {
    requestAnimationFrame(function () {
      requestAnimationFrame(
        initV6EditorialDetails
      );
    });
  }


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      scheduleInit,
      { once: true }
    );
  } else {
    scheduleInit();
  }


  window.addEventListener(
    'resize',
    scheduleDecorLayout,
    { passive: true }
  );


  document.addEventListener(
    'shopify:section:load',
    scheduleInit
  );

})();


/* =========================================================
   6. UCMPC V30 — ICONOGRAFÍA LATERAL MÁS ESPACIADA
   Fundación: 48 íconos
   Historias que Fortalecen: 30 íconos
   Impacto: 24 íconos
   Distribución más orgánica y desordenada en las 3 páginas
   ========================================================= */

(function () {
  const MIN_WIDTH = 1180;
  const DECOR_CLASS = 'ucmpc-page-decor-v7';
  const ICON_CLASS = 'ucmpc-page-decor-v7__icon';

  const ICON_COUNTS = {
    foundation: 48,
    strengthening: 30,
    impact: 24
  };

  const ICONS_V15 = {
    book:
      '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M7 10.5c6.3-1.4 11.8.2 17 4.7v24.3c-5.2-4.5-10.7-6.1-17-4.7z"/><path d="M41 10.5c-6.3-1.4-11.8.2-17 4.7v24.3c5.2-4.5 10.7-6.1 17-4.7z"/></svg>',

    pencil:
      '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M10 35.5 34.7 10.8a3.1 3.1 0 0 1 4.4 0l.1.1a3.1 3.1 0 0 1 0 4.4L14.5 40z"/><path d="m10 35.5-1.7 6.2 6.2-1.7"/><path d="m31.8 13.7 4.5 4.5"/></svg>',

    quill:
      '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M39 8C24 9 13 18 10 38c8-8 16-15 26-23"/><path d="M13 34c7 0 14-3 19-8"/><path d="M10 38h10"/></svg>',

    page:
      '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M12 7h17l8 8v26H12z"/><path d="M29 7v9h8"/><path d="M17 23h15M17 29h15M17 35h10"/></svg>',

    quote:
      '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M10 18h11v10c0 7-4 11-10 12"/><path d="M27 18h11v10c0 7-4 11-10 12"/></svg>',

    leaf:
      '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M39 9C22 10 12 18 10 36c15 2 27-8 29-27z"/><path d="M13 34c7-7 13-12 23-19"/></svg>',

    sparkle:
      '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M24 8c1.7 8.5 5.5 12.3 14 14-8.5 1.7-12.3 5.5-14 14-1.7-8.5-5.5-12.3-14-14 8.5-1.7 12.3-5.5 14-14z"/><path d="M38 7v8M42 11h-8"/></svg>',

    link:
      '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M19 29.5 14.5 34a7 7 0 0 1-9.9-9.9l6.2-6.2a7 7 0 0 1 9.9 0"/><path d="m29 18.5 4.5-4.5a7 7 0 0 1 9.9 9.9l-6.2 6.2a7 7 0 0 1-9.9 0"/><path d="m17 31 14-14"/></svg>',

    sprout:
      '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M24 39V22"/><path d="M24 27c-8 0-13-4-14-11 8-1 13 3 14 11z"/><path d="M24 22c1-8 6-12 14-11-1 7-6 11-14 11z"/></svg>',

    ripple:
      '<svg viewBox="0 0 48 48" aria-hidden="true"><circle cx="24" cy="24" r="4"/><circle cx="24" cy="24" r="11"/><circle cx="24" cy="24" r="18"/></svg>',

    path:
      '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M9 36c7-17 11-8 17-19 4-7 8-8 13-5"/><path d="M34 8l5 4-4 5"/><circle cx="9" cy="36" r="2.2"/></svg>',

    heart:
      '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M24 39S8 30 8 18.5C8 12.7 12.1 9 17 9c3.1 0 5.7 1.6 7 4 1.3-2.4 3.9-4 7-4 4.9 0 9 3.7 9 9.5C40 30 24 39 24 39z"/></svg>',

    people:
      '<svg viewBox="0 0 48 48" aria-hidden="true"><circle cx="24" cy="14" r="5"/><circle cx="12" cy="20" r="4"/><circle cx="36" cy="20" r="4"/><path d="M14 38c1-8 5-12 10-12s9 4 10 12"/><path d="M4 38c1-6 4-9 8-9 2 0 4 .8 5.5 2.4"/><path d="M44 38c-1-6-4-9-8-9-2 0-4 .8-5.5 2.4"/></svg>',

    dialogue:
      '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M8 10h27a5 5 0 0 1 5 5v13a5 5 0 0 1-5 5H21l-9 7v-7H8a5 5 0 0 1-5-5V15a5 5 0 0 1 5-5z"/><path d="M13 20h17M13 25h12"/></svg>',

    hands:
      '<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M8 27c5-1 8 1 11 5l4 5"/><path d="M40 27c-5-1-8 1-11 5l-4 5"/><path d="M16 19c2-4 5-6 8-6s6 2 8 6"/><path d="M13 24c3 0 6 2 11 8 5-6 8-8 11-8"/></svg>',

    circles:
      '<svg viewBox="0 0 48 48" aria-hidden="true"><circle cx="18" cy="24" r="11"/><circle cx="30" cy="24" r="11"/></svg>'
  };

  const SETS_V15 = {
    foundation: [
      'book', 'quill', 'pencil', 'page',
      'quote', 'people', 'dialogue', 'leaf',
      'sparkle', 'hands', 'circles', 'heart',
      'book', 'path', 'sprout', 'pencil'
    ],

    strengthening: [
      'people', 'hands', 'link', 'dialogue',
      'book', 'circles', 'heart', 'sprout',
      'quote', 'leaf', 'page', 'sparkle',
      'link', 'people', 'path', 'book'
    ],

    impact: [
      'ripple', 'people', 'sprout', 'path',
      'circles', 'book', 'hands', 'leaf',
      'sparkle', 'dialogue', 'heart', 'page',
      'ripple', 'path', 'people', 'sprout'
    ]
  };

  function getEditorial() {
    return document.querySelector('.ucmpc-editorial-page');
  }

  function getType(editorial) {
    if (!editorial) return null;

    if (editorial.classList.contains('ucmpc-page-la-fundacion')) {
      return 'foundation';
    }

    if (editorial.classList.contains('ucmpc-page-historias-que-fortalecen')) {
      return 'strengthening';
    }

    if (editorial.classList.contains('ucmpc-page-impacto')) {
      return 'impact';
    }

    return null;
  }

  function randomFactory(seed) {
    let value = seed >>> 0;

    return function () {
      value += 0x6D2B79F5;
      let t = value;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return (((t ^ (t >>> 14)) >>> 0) / 4294967296);
    };
  }

  function seedFor(type) {
    if (type === 'foundation') return 9157;
    if (type === 'strengthening') return 13249;
    return 18061;
  }

  function shuffleBalancedSides(count, rand) {
    const leftCount = Math.ceil(count / 2);
    const sides = Array.from({ length: count }, function (_, index) {
      return index < leftCount ? 'left' : 'right';
    });

    for (let i = sides.length - 1; i > 0; i -= 1) {
      const j = Math.floor(rand() * (i + 1));
      const tmp = sides[i];
      sides[i] = sides[j];
      sides[j] = tmp;
    }

    return sides;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function isTooClose(candidate, placed, minDistance) {
    return placed.some(function (existing) {
      const dx = existing.x - candidate.x;
      const dy = existing.y - candidate.y;
      return Math.sqrt(dx * dx + dy * dy) < minDistance;
    });
  }

  /*
   * Distribución libre tipo "best candidate".
   * No usa diagonales, ondas ni bandas secuenciales: para cada ícono
   * prueba muchas posiciones aleatorias y conserva la que mejor llena
   * el espacio disponible sin acercarse demasiado a los demás.
   *
   * Algunos íconos pueden quedar visualmente vecinos, pero nunca
   * superpuestos ni pegados al borde exterior de la página.
   */
  function buildFreeformPositions(count, xMin, xMax, topSafe, bottomSafe, rand) {
    const positions = [];
    const usableWidth = Math.max(1, xMax - xMin);
    const usableHeight = Math.max(1, bottomSafe - topSafe);
    const area = usableWidth * usableHeight;

    /*
     * La distancia mínima se adapta al espacio real.
     * Con esto Fundación puede mantener sus 48 íconos sin amontonarse,
     * mientras las páginas más cortas conservan aire suficiente.
     */
    const idealDistance = Math.sqrt(area / Math.max(1, count)) * 0.78;
    const hardMinDistance = clamp(idealDistance, 66, 108);

    function nearestDistance(candidate) {
      if (!positions.length) return Number.POSITIVE_INFINITY;

      let nearest = Number.POSITIVE_INFINITY;

      positions.forEach(function (existing) {
        const dx = existing.x - candidate.x;
        const dy = existing.y - candidate.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        nearest = Math.min(nearest, distance);
      });

      return nearest;
    }

    for (let index = 0; index < count; index += 1) {
      let bestCandidate = null;
      let bestScore = -1;

      /*
       * Más candidatos = distribución más natural y menos colisiones.
       */
      const candidateCount = positions.length < 8 ? 70 : 110;

      for (let attempt = 0; attempt < candidateCount; attempt += 1) {
        const candidate = {
          x: xMin + rand() * usableWidth,
          y: topSafe + rand() * usableHeight
        };

        const nearest = nearestDistance(candidate);

        /*
         * Penalizamos levemente los extremos verticales para evitar
         * cúmulos pegados arriba/abajo, sin crear una retícula visible.
         */
        const topRoom = candidate.y - topSafe;
        const bottomRoom = bottomSafe - candidate.y;
        const edgeRoom = Math.min(topRoom, bottomRoom);
        const edgeBonus = Math.min(edgeRoom, 120) * 0.08;

        const score = nearest + edgeBonus;

        if (score > bestScore) {
          bestScore = score;
          bestCandidate = candidate;
        }
      }

      /*
       * En el caso improbable de que el espacio sea muy reducido,
       * elegimos igualmente la mejor posición disponible.
       */
      if (!bestCandidate) {
        bestCandidate = {
          x: xMin + rand() * usableWidth,
          y: topSafe + rand() * usableHeight
        };
      }

      /*
       * Si la mejor opción todavía queda demasiado cerca, hacemos una
       * segunda pasada enfocada únicamente en separación.
       */
      if (nearestDistance(bestCandidate) < hardMinDistance) {
        for (let attempt = 0; attempt < 120; attempt += 1) {
          const candidate = {
            x: xMin + rand() * usableWidth,
            y: topSafe + rand() * usableHeight
          };

          if (nearestDistance(candidate) >= hardMinDistance) {
            bestCandidate = candidate;
            break;
          }
        }
      }

      positions.push(bestCandidate);
    }

    /*
     * Mezcla final: el orden del DOM nunca revela la posición visual.
     */
    for (let i = positions.length - 1; i > 0; i -= 1) {
      const j = Math.floor(rand() * (i + 1));
      const tmp = positions[i];
      positions[i] = positions[j];
      positions[j] = tmp;
    }

    return positions;
  }

  function ensureDecor(type) {
    let decor = document.querySelector('.' + DECOR_CLASS);
    const iconCount = ICON_COUNTS[type];

    if (!decor) {
      decor = document.createElement('div');
      decor.className = DECOR_CLASS;
      decor.setAttribute('aria-hidden', 'true');
      document.body.appendChild(decor);
    }

    const currentType = decor.dataset.pageType;

    if (currentType !== type || decor.children.length !== iconCount) {
      decor.replaceChildren();
      decor.dataset.pageType = type;

      const iconSet = SETS_V15[type];

      Array.from({ length: iconCount }).forEach(function (_, index) {
        const name = iconSet[index % iconSet.length];
        const icon = document.createElement('span');
        icon.className = ICON_CLASS;
        icon.dataset.index = String(index);
        icon.dataset.icon = name;
        icon.innerHTML = ICONS_V15[name];
        decor.appendChild(icon);
      });
    }

    return decor;
  }

  function layoutDecor(editorial, type, decor) {
    if (!editorial || !type || !decor) return;

    const viewportWidth = window.innerWidth;

    if (viewportWidth < MIN_WIDTH) {
      decor.hidden = true;
      return;
    }

    const main = document.querySelector('#MainContent');
    if (!main) {
      decor.hidden = true;
      return;
    }

    decor.hidden = false;

    const docHeight = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight
    );

    decor.style.setProperty(
      '--ucmpc-page-decor-height',
      docHeight + 'px'
    );

    const mainRect = main.getBoundingClientRect();
    const mainTop = mainRect.top + window.scrollY;
    const mainBottom = mainTop + Math.max(main.scrollHeight, mainRect.height);

    const panelLeft = mainRect.left;
    const panelRight = mainRect.right;

    /*
     * Dejamos una franja respirada tanto contra el borde de la ventana
     * como contra el panel editorial central.
     */
    const outer = clamp(viewportWidth * 0.052, 66, 104);
    const gap = 76;
    const nominalSize = 38;
    const maxDrift = 28;

    const leftMin = outer;
    const leftMax = panelLeft - gap - nominalSize - maxDrift;
    const rightMin = panelRight + gap + maxDrift;
    const rightMax = viewportWidth - outer - nominalSize;

    if (leftMax <= leftMin || rightMax <= rightMin) {
      decor.hidden = true;
      return;
    }

    const icons = Array.from(decor.querySelectorAll('.' + ICON_CLASS));
    const rand = randomFactory(seedFor(type));
    const sides = shuffleBalancedSides(icons.length, rand);

    const topSafe = mainTop + 58;
    const bottomSafe = Math.max(topSafe + 420, mainBottom - 58);

    const leftIcons = [];
    const rightIcons = [];

    icons.forEach(function (icon, index) {
      const item = { icon, index };
      if (sides[index] === 'left') {
        leftIcons.push(item);
      } else {
        rightIcons.push(item);
      }
    });

    const leftPositions = buildFreeformPositions(
      leftIcons.length,
      leftMin,
      leftMax,
      topSafe,
      bottomSafe,
      rand
    );

    const rightPositions = buildFreeformPositions(
      rightIcons.length,
      rightMin,
      rightMax,
      topSafe,
      bottomSafe,
      rand
    );

    function applyPositions(items, positions) {
      items.forEach(function (item, positionIndex) {
        const icon = item.icon;
        const position = positions[positionIndex];
        const size = 28 + Math.round(rand() * 15);
        const rotate = -18 + rand() * 36;

        icon.style.setProperty('--ucmpc-v7-x', position.x.toFixed(1) + 'px');
        icon.style.setProperty('--ucmpc-v7-y', position.y.toFixed(1) + 'px');
        icon.style.setProperty('--ucmpc-v7-size', size + 'px');
        icon.style.setProperty('--ucmpc-v7-rotate', rotate.toFixed(1) + 'deg');

        /*
         * Movimiento visible pero contenido: suficiente para dar vida
         * sin que dos íconos separados terminen cruzándose.
         */
        icon.style.setProperty('--ucmpc-v7-dx1', ((rand() - 0.5) * 30).toFixed(1) + 'px');
        icon.style.setProperty('--ucmpc-v7-dx2', ((rand() - 0.5) * 36).toFixed(1) + 'px');
        icon.style.setProperty('--ucmpc-v7-dx3', ((rand() - 0.5) * 32).toFixed(1) + 'px');

        icon.style.setProperty('--ucmpc-v7-dy1', (-12 - rand() * 15).toFixed(1) + 'px');
        icon.style.setProperty('--ucmpc-v7-dy2', (9 + rand() * 16).toFixed(1) + 'px');
        icon.style.setProperty('--ucmpc-v7-dy3', (-8 - rand() * 14).toFixed(1) + 'px');

        icon.style.setProperty('--ucmpc-v7-duration', (17 + rand() * 10).toFixed(1) + 's');
        icon.style.setProperty('--ucmpc-v7-delay', (-rand() * 13).toFixed(1) + 's');

        /* Un poco más visibles que V15, sin competir con el contenido. */
        icon.style.setProperty('--ucmpc-v7-opacity', (0.215 + rand() * 0.075).toFixed(3));
      });
    }

    applyPositions(leftIcons, leftPositions);
    applyPositions(rightIcons, rightPositions);
  }

  let resizeTimer = null;

  function initV15Decor() {
    const editorial = getEditorial();
    const type = getType(editorial);

    if (!editorial || !type) {
      document.querySelector('.' + DECOR_CLASS)?.remove();
      return;
    }

    const decor = ensureDecor(type);
    layoutDecor(editorial, type, decor);
  }

  function scheduleLayout() {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(initV15Decor, 100);
  }

  function start() {
    requestAnimationFrame(function () {
      requestAnimationFrame(initV15Decor);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('resize', scheduleLayout, { passive: true });
  window.addEventListener('load', scheduleLayout, { once: true });
  document.addEventListener('shopify:section:load', start);
})();

/* =========================================================
   6. UCMPC V17 — PORTADAS ANIMADAS EN FICHA DE CUENTO
   Exclusivo para las dos URLs de producto.
   No reemplaza imágenes de Shopify ni modifica colección,
   carrito, checkout, búsqueda, regalos o Home.
   ========================================================= */

(function () {
  const STORIES = {
    '/products/la-ciudad-espejo': {
      video: 'https://cdn.shopify.com/videos/c/o/v/04039cecc71240fbbddb33cb1db6c7f0.mp4'
    },
    '/products/el-huesped-de-las-cosas-pequenas': {
      video: 'https://cdn.shopify.com/videos/c/o/v/3bd0ad38c45a4491ac4be3d69b82e1e2.mp4'
    },
    '/products/20-21': {
      video: 'https://cdn.shopify.com/videos/c/o/v/9cfa8daf62bd4e238d6dd8e9e88de519.mp4'
    },
    '/products/pronostico-reservado': {
      video: 'https://cdn.shopify.com/videos/c/o/v/00413ac9d2f94a089dfe5a23e97fafc8.mp4'
    },
    '/products/sala-de-evidencias': {
      video: 'https://cdn.shopify.com/videos/c/o/v/abadefd8f596443183ebc5f021b8a869.mp4'
    }
  };

  let observer = null;
  let retryTimer = null;
  let mutationObserver = null;
  let attempts = 0;

  function normalizedPath() {
    return window.location.pathname.replace(/\/$/, '');
  }

  function getStory() {
    return STORIES[normalizedPath()] || null;
  }

  function isProductStoryPage() {
    return Boolean(getStory());
  }

  function findPrimaryFrame() {
    if (!isProductStoryPage()) return null;

    const root =
      document.querySelector('#MainContent .product-information__media') ||
      document.querySelector('#MainContent media-gallery');

    if (!root) return null;

    const candidates = Array.from(
      root.querySelectorAll('.product-media-container')
    );

    if (candidates.length) {
      const visible = candidates.find(function (element) {
        const rect = element.getBoundingClientRect();
        return rect.width > 80 && rect.height > 80;
      });

      return visible || candidates[0];
    }

    return root.querySelector('.product-media') || null;
  }

  function cleanupRuntime() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }

    if (mutationObserver) {
      mutationObserver.disconnect();
      mutationObserver = null;
    }

    if (retryTimer) {
      window.clearTimeout(retryTimer);
      retryTimer = null;
    }
  }

  function removeUcmpcStoryVideos() {
    document
      .querySelectorAll('.ucmpc-story-cover-video')
      .forEach(function (video) {
        try { video.pause(); } catch (error) {}
        video.remove();
      });

    document
      .querySelectorAll('.ucmpc-story-media-frame')
      .forEach(function (frame) {
        frame.classList.remove('ucmpc-story-media-frame');
      });
  }

  function playSafely(video) {
    try {
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(function () {});
      }
    } catch (error) {}
  }

  function attachVideo(frame, story) {
    if (!frame || !story) return false;

    frame.classList.add('ucmpc-story-media-frame');

    let video = frame.querySelector(':scope > .ucmpc-story-cover-video');

    if (!video) {
      video = document.createElement('video');
      video.className = 'ucmpc-story-cover-video';
      video.muted = true;
      video.loop = true;
      video.autoplay = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('aria-hidden', 'true');
      video.setAttribute('tabindex', '-1');

      const source = document.createElement('source');
      source.src = story.video;
      source.type = 'video/mp4';
      video.appendChild(source);

      frame.appendChild(video);

      const reveal = function () {
        video.classList.add('is-ready');
        playSafely(video);
      };

      video.addEventListener('loadeddata', reveal, { once: true });
      video.addEventListener('canplay', reveal, { once: true });

      video.addEventListener('error', function () {
        frame.classList.remove('ucmpc-story-media-frame');
        video.remove();
      }, { once: true });

      video.load();
    }

    if (observer) observer.disconnect();

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              playSafely(video);
            } else {
              try { video.pause(); } catch (error) {}
            }
          });
        },
        { threshold: 0.08 }
      );

      observer.observe(frame);
    } else {
      playSafely(video);
    }

    return true;
  }

  function tryInit() {
    const story = getStory();

    if (!story) {
      cleanupRuntime();
      removeUcmpcStoryVideos();
      return;
    }

    /* Respeta preferencia de movimiento reducido. */
    if (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      removeUcmpcStoryVideos();
      return;
    }

    const frame = findPrimaryFrame();

    if (frame && attachVideo(frame, story)) {
      attempts = 0;
      if (mutationObserver) {
        mutationObserver.disconnect();
        mutationObserver = null;
      }
      return;
    }

    attempts += 1;

    if (attempts <= 20) {
      retryTimer = window.setTimeout(tryInit, 180);
    }
  }

  function watchHydration() {
    if (!isProductStoryPage() || !('MutationObserver' in window)) {
      return;
    }

    if (mutationObserver) mutationObserver.disconnect();

    mutationObserver = new MutationObserver(function () {
      const frame = findPrimaryFrame();
      if (!frame) return;

      if (!frame.querySelector('.ucmpc-story-cover-video')) {
        attempts = 0;
        tryInit();
      }
    });

    const main = document.querySelector('#MainContent');
    if (main) {
      mutationObserver.observe(main, {
        childList: true,
        subtree: true
      });
    }
  }

  function start() {
    cleanupRuntime();
    attempts = 0;

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        tryInit();
        watchHydration();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('pageshow', start);
  document.addEventListener('shopify:section:load', start);
})();



/* =========================================================
   7. UCMPC V23 — CUENTOS DE VIDA / SIN REORDENAR EL DOM
   La estructura ya se resuelve en Liquid + collection.json.
   Solo marcamos la sección editorial inferior para conservar
   el lenguaje visual aprobado, sin mover nodos ni provocar titileo.
   ========================================================= */

(function () {
  const PATH = '/collections/cuentos-de-vida';

  function normalizedPath() {
    return window.location.pathname.replace(/\/$/, '');
  }

  function markEditorialIntro() {
    if (normalizedPath() !== PATH) return;

    const main = document.querySelector('#MainContent');
    if (!main) return;

    const sections = Array.from(
      main.querySelectorAll(':scope > .shopify-section')
    );

    const intro = sections.find(function (section) {
      const heading = section.querySelector('h1');
      const text = (heading?.textContent || '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

      return text === 'cuentos de vida';
    });

    if (intro) {
      intro.classList.add('ucmpc-library-intro-after');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      markEditorialIntro,
      { once: true }
    );
  } else {
    markEditorialIntro();
  }

  document.addEventListener(
    'shopify:section:load',
    markEditorialIntro
  );
})();


/* =========================================================
   9. UCMPC V20 — SINOPSIS ORIGINALES
   Texto fiel al material original de cada cuento.
   Exclusivo para las dos fichas actuales.
   ========================================================= */

(function () {
  const STORIES = {
    '/products/la-ciudad-espejo': {
      paragraphs: [
        'Durante años, pensé que, si algún día publicaba lo que escribo, comenzaría por contar mi vida: lo que ha significado habitar un cuerpo atravesado por la fragilidad, crecer con Osteogénesis Imperfecta y aprender a sostenerme en un mundo que no siempre sabe cómo mirar la diferencia. Pero la historia que terminó abriendo esta serie fue otra. No la del primer hueso fracturado, ni la de la adaptación, ni la de la fuerza aprendida. Fue la del amor. O, más exactamente, la del primer amor y su fractura.',
        'La ciudad espejo es el inicio de una serie de cuentos nacidos de esa grieta. Historias donde la experiencia real se transforma en ficción para decir mejor lo que a veces la realidad, sola, no alcanza a explicar. Aquí, César vive una vida admirable, ordenada y funcional, hasta que la aparición de Liora no sólo le abre el amor, sino también una fisura en la realidad: una calle que no debería existir, una ciudad secreta que aparece entre la ciudad común, y un mundo donde descubre, por primera vez, una versión más amplia, más viva y más luminosa de sí mismo.',
        'Pero lo que empieza como revelación pronto se vuelve hechizo. Y cuando Liora comienza a retirarse y la ciudad espejo deja de devolver promesas para empezar a devolver obsesiones, César tendrá que enfrentarse a una verdad más difícil que la pérdida: que hay amores que no vienen a quedarse, sino a abrir una puerta.',
        'Este no es un cuento con moraleja. Tampoco una historia de superación típica. Es una historia íntima, visual y herida sobre el deseo, la culpa, el hundimiento y la decisión de seguir viviendo cuando ya no es posible volver a ser el de antes. Y es, también, la primera puerta de un universo narrativo donde escribir no significa haber entendido, sino atreverse a contar mientras todavía se intenta hacerlo.'
      ]
    },
    '/products/el-huesped-de-las-cosas-pequenas': {
      paragraphs: [
        'César sólo iba a una cita de trabajo.',
        'Un correo breve. Una dirección desconocida. Una oficina en el cuarto piso. Nada que justificara sospechar que, al entrar al Edificio Umbral, algo en su vida iba a quedar detenido entre dos pisos.',
        'El ascensor se traba.',
        'Y César no está solo.',
        'Lo que empieza como una espera incómoda se convierte en una conversación que César no pidió y que, tal vez, lleva años evitando. Cada piso que se abre parece conocer demasiado de él: una sala de infancia, una escalera imposible, una habitación con una silla vacía, una puerta marcada con un año que César no quiere volver a mirar.',
        'Halo no da respuestas. Hace preguntas.',
        'Y algunas preguntas tienen la mala costumbre de abrir lugares que uno creía cerrados.',
        'El huésped de las cosas pequeñas es la historia de un hombre atrapado en un edificio que no debería existir del todo, frente a alguien que parece saber más de lo conveniente. Un cuento sobre las heridas que uno aprende a explicar demasiado bien, las ausencias que siguen ocupando espacio y esas cosas mínimas que, sin hacer esfuerzo, pueden sostener una vida entera.',
        'Porque a veces lo que más cambia a una persona no es una revelación.',
        'Es una conversación que no estaba en los pronósticos de nadie.'
      ]
    },
    '/products/20-21': {
      paragraphs: [
        "César despierta desnudo sobre una plancha metálica, dentro de un cuerpo que no reconoce. Grande. Pesado. Ajeno. Sin embargo, sabe que ese hombre es él.",
        "En la pared, un reloj detenido marca 20:21. En el tobillo, una etiqueta: Paciente en devolución. Nombre pendiente. Vida por confirmar.",
        "Mientras médicos y familia intentan traerlo de vuelta después de un accidente que apenas existe en fragmentos, otra versión de César continúa trabajando, respondiendo, cumpliendo. Es el hombre funcional: el que aprendió a seguir incluso cuando algo dentro se estaba rompiendo. Y parece dispuesto a regresar en su lugar.",
        "Entre la sedación, la afasia, un cuerpo que no responde y una dimensión donde las vidas posibles adquieren forma, César tendrá que descubrir si sobrevivir significa recuperar todo lo que era o escoger qué partes merecen volver con él.",
        "20:21 es un cuento sobre el borde de la muerte, el miedo que a veces se disfraza de cuidado, las decisiones aplazadas y esa zona incómoda donde seguir funcionando deja de ser lo mismo que estar vivo. No hay una segunda oportunidad. Hay una hora que insiste, un pulgar inmóvil y una pregunta: si logra volver, ¿a qué vida quiere regresar?"
      ]
    },
    '/products/pronostico-reservado': {
      paragraphs: [
        'A Alba y Liam les entregan a un recién nacido y, casi al mismo tiempo, un futuro contado en voz baja. Los médicos hablan de probabilidades escasas, de una fragilidad difícil de anticipar y de una expectativa de vida que nadie se atreve a proyectar demasiado lejos. Antes de que el niño pueda construir un recuerdo, otros ya han empezado a imaginar su ausencia.',
        'Pronto descubren que la incertidumbre atrae consejos, pero también algo más incómodo: lástima, miedo y esa clase de prudencia que a veces se parece demasiado a la vergüenza. Familiares, amigos y especialistas parecen saber cómo protegerlo, cuánto esperar de él y hasta qué tan lejos conviene hacer planes. Alba y Liam escuchan, dudan y terminan enfrentando una pregunta que nadie puede responder por ellos: ¿se cría a un niño para evitar que muera o para permitirle vivir?'
      ]
    },
    '/products/sala-de-evidencias': {
      paragraphs: [
        'Una llamada inesperada lleva a César hasta un museo que prepara una exposición sobre el dolor humano. Los curadores buscan una tabla de madera que lo acompañó durante algunas de sus fracturas de infancia. Para ellos, es una pieza extraordinaria: una prueba concreta de algo que ocurrió.',
        'Pero cuando la tabla llega al museo, empiezan a aparecer sobre ella frases que nadie sabe clasificar. Hablan de heridas sin radiografías, pérdidas sin cicatrices y sufrimientos para los que no existe una vitrina adecuada.',
        'César, acostumbrado a que su propio dolor sea visible y verificable, tendrá que enfrentarse también a las veces en que él mismo desestimó lo que otros sentían.',
        'Sala de evidencias es un cuento sobre los dolores que reconocemos, los que ponemos en duda y la necesidad de dejar de exigir pruebas para acompañar a alguien.'
      ]
    }
  };

  function normalizedPath() {
    return window.location.pathname.replace(/\/$/, '');
  }

  function buildSynopsis(story) {
    const section = document.createElement('section');
    section.className = 'ucmpc-story-synopsis';
    section.setAttribute('aria-label', 'Sinopsis');

    const heading = document.createElement('h2');
    heading.className = 'ucmpc-story-synopsis__title';
    heading.textContent = 'Sinopsis';

    const copy = document.createElement('div');
    copy.className = 'ucmpc-story-synopsis__copy';

    story.paragraphs.forEach(function (text) {
      const paragraph = document.createElement('p');
      paragraph.textContent = text;
      copy.appendChild(paragraph);
    });

    section.append(heading, copy);
    return section;
  }

  function mountSynopsis() {
    const story = STORIES[normalizedPath()];
    if (!story) return;

    const main = document.querySelector('#MainContent');
    const product = main?.querySelector('.product-information');

    if (!main || !product) return;

    main
      .querySelectorAll('.ucmpc-story-synopsis')
      .forEach(function (existing) {
        existing.remove();
      });

    const synopsis = buildSynopsis(story);
    product.insertAdjacentElement('afterend', synopsis);
  }

  function start() {
    requestAnimationFrame(function () {
      requestAnimationFrame(mountSynopsis);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('pageshow', start);
  document.addEventListener('shopify:section:load', start);
})();



/* =========================================================
   10. UCMPC V31 — ATMÓSFERA EDITORIAL ABSTRACTA
   Fundación · Historias que Fortalecen · Impacto

   Reemplaza la iconografía figurativa por recursos editoriales
   abstractos. No modifica contenido ni estructura de las páginas.
   ========================================================= */

(function () {
  const CLASS = 'ucmpc-editorial-atmosphere';
  const ITEM_CLASS = 'ucmpc-editorial-atmosphere__item';
  const MIN_WIDTH = 1080;

  const TRACE_A =
    '<svg viewBox="0 0 180 96" aria-hidden="true">' +
      '<path d="M7 72C36 31 65 87 96 46C119 16 144 20 173 8"/>' +
    '</svg>';

  const TRACE_B =
    '<svg viewBox="0 0 180 96" aria-hidden="true">' +
      '<path d="M7 18C39 62 65 11 97 48C119 74 145 82 173 58"/>' +
    '</svg>';

  const TRACE_C =
    '<svg viewBox="0 0 180 96" aria-hidden="true">' +
      '<path d="M7 62C33 62 42 20 71 22C102 24 95 77 128 75C147 74 158 52 173 39"/>' +
    '</svg>';

  const CONFIG = {
    foundation: {
      seed: 1731,
      words: ['ORIGEN', 'PROPÓSITO'],
      glyphs: ['“', ')'],
      numbers: ['01', '02'],
      traces: [TRACE_A, TRACE_C, TRACE_B, TRACE_A],
      rules: 4,
      marks: 4
    },
    strengthening: {
      seed: 2849,
      words: ['VÍNCULO', 'CONFIANZA'],
      glyphs: ['(', '”'],
      numbers: ['01', '02'],
      traces: [TRACE_B, TRACE_A, TRACE_C],
      rules: 4,
      marks: 3
    },
    impact: {
      seed: 3917,
      words: ['POSIBILIDAD', 'HUELLA'],
      glyphs: ['—', ')'],
      numbers: ['01', '02'],
      traces: [TRACE_C, TRACE_B, TRACE_A],
      rules: 3,
      marks: 3
    }
  };

  function normalizePath() {
    return window.location.pathname.replace(/\/$/, '');
  }

  function getEditorial() {
    return document.querySelector('.ucmpc-editorial-page');
  }

  function getType(editorial) {
    if (!editorial) return null;

    if (editorial.classList.contains('ucmpc-page-la-fundacion')) {
      return 'foundation';
    }

    if (editorial.classList.contains('ucmpc-page-historias-que-fortalecen')) {
      return 'strengthening';
    }

    if (editorial.classList.contains('ucmpc-page-impacto')) {
      return 'impact';
    }

    return null;
  }

  function randomFactory(seed) {
    let value = seed >>> 0;

    return function () {
      value += 0x6D2B79F5;
      let t = value;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function removeLegacyDecor() {
    document
      .querySelectorAll(
        '.ucmpc-page-decor-v7, ' +
        '.ucmpc-editorial-gutter-decor, ' +
        '.ucmpc-editorial-side-decor, ' +
        '.ucmpc-editorial-ambient'
      )
      .forEach(function (node) {
        node.remove();
      });
  }

  function removeAtmosphere() {
    document
      .querySelectorAll('.' + CLASS)
      .forEach(function (node) {
        node.remove();
      });
  }

  function buildItem(type, content) {
    const item = document.createElement('span');
    item.className = ITEM_CLASS + ' ' + ITEM_CLASS + '--' + type;
    item.setAttribute('aria-hidden', 'true');

    if (type === 'trace') {
      item.innerHTML = content;
    } else if (content !== undefined && content !== null) {
      item.textContent = content;
    }

    return item;
  }

  function ensureAtmosphere(pageType) {
    let atmosphere = document.querySelector('.' + CLASS);

    if (!atmosphere) {
      atmosphere = document.createElement('div');
      atmosphere.className = CLASS;
      atmosphere.setAttribute('aria-hidden', 'true');
      document.body.appendChild(atmosphere);
    }

    if (atmosphere.dataset.pageType === pageType) {
      return atmosphere;
    }

    atmosphere.replaceChildren();
    atmosphere.dataset.pageType = pageType;

    const config = CONFIG[pageType];

    config.words.forEach(function (word) {
      atmosphere.appendChild(buildItem('word', word));
    });

    config.glyphs.forEach(function (glyph) {
      atmosphere.appendChild(buildItem('glyph', glyph));
    });

    config.numbers.forEach(function (number) {
      atmosphere.appendChild(buildItem('number', number));
    });

    config.traces.forEach(function (trace) {
      atmosphere.appendChild(buildItem('trace', trace));
    });

    Array.from({ length: config.rules }).forEach(function () {
      atmosphere.appendChild(buildItem('rule'));
    });

    Array.from({ length: config.marks }).forEach(function () {
      atmosphere.appendChild(buildItem('mark'));
    });

    return atmosphere;
  }

  function itemDimensions(item, rand) {
    if (item.classList.contains(ITEM_CLASS + '--word')) {
      const font = 62 + Math.round(rand() * 26);
      item.style.setProperty('--ucmpc-atm-font-size', font + 'px');
      return { width: Math.round(font * 5.8), height: font };
    }

    if (item.classList.contains(ITEM_CLASS + '--glyph')) {
      const font = 92 + Math.round(rand() * 44);
      item.style.setProperty('--ucmpc-atm-font-size', font + 'px');
      return { width: Math.round(font * 0.78), height: font };
    }

    if (item.classList.contains(ITEM_CLASS + '--trace')) {
      const width = 112 + Math.round(rand() * 70);
      const height = Math.round(width * 0.53);
      item.style.setProperty('--ucmpc-atm-width', width + 'px');
      item.style.setProperty('--ucmpc-atm-height', height + 'px');
      return { width, height };
    }

    if (item.classList.contains(ITEM_CLASS + '--rule')) {
      const width = 74 + Math.round(rand() * 96);
      item.style.setProperty('--ucmpc-atm-width', width + 'px');
      return { width, height: 8 };
    }

    if (item.classList.contains(ITEM_CLASS + '--mark')) {
      return { width: 22, height: 22 };
    }

    return { width: 48, height: 18 };
  }

  function overlapScore(candidate, placed) {
    let nearest = Number.POSITIVE_INFINITY;

    placed.forEach(function (existing) {
      const cx = candidate.x + candidate.width / 2;
      const cy = candidate.y + candidate.height / 2;
      const ex = existing.x + existing.width / 2;
      const ey = existing.y + existing.height / 2;
      const dx = cx - ex;
      const dy = cy - ey;
      const distance = Math.sqrt(dx * dx + dy * dy);
      nearest = Math.min(nearest, distance);
    });

    return nearest;
  }

  function pickPosition(bounds, size, placed, rand) {
    let best = null;
    let bestScore = -1;

    const xMin = bounds.xMin;
    const xMax = Math.max(xMin, bounds.xMax - size.width);
    const yMin = bounds.yMin;
    const yMax = Math.max(yMin, bounds.yMax - size.height);

    for (let attempt = 0; attempt < 90; attempt += 1) {
      const candidate = {
        x: xMin + rand() * Math.max(1, xMax - xMin),
        y: yMin + rand() * Math.max(1, yMax - yMin),
        width: size.width,
        height: size.height
      };

      const separation = placed.length
        ? overlapScore(candidate, placed)
        : 9999;

      const score = separation + rand() * 18;

      if (score > bestScore) {
        best = candidate;
        bestScore = score;
      }
    }

    return best || {
      x: xMin,
      y: yMin,
      width: size.width,
      height: size.height
    };
  }

  function applyMotion(item, rand) {
    const rotation = -8 + rand() * 16;
    item.style.setProperty('--ucmpc-atm-rotate', rotation.toFixed(2) + 'deg');

    item.style.setProperty('--ucmpc-atm-dx1', ((rand() - .5) * 20).toFixed(1) + 'px');
    item.style.setProperty('--ucmpc-atm-dy1', ((rand() - .5) * 24).toFixed(1) + 'px');
    item.style.setProperty('--ucmpc-atm-dx2', ((rand() - .5) * 22).toFixed(1) + 'px');
    item.style.setProperty('--ucmpc-atm-dy2', ((rand() - .5) * 20).toFixed(1) + 'px');
    item.style.setProperty('--ucmpc-atm-dx3', ((rand() - .5) * 16).toFixed(1) + 'px');
    item.style.setProperty('--ucmpc-atm-dy3', ((rand() - .5) * 18).toFixed(1) + 'px');

    item.style.setProperty('--ucmpc-atm-dr1', ((rand() - .5) * 1.8).toFixed(2) + 'deg');
    item.style.setProperty('--ucmpc-atm-dr2', ((rand() - .5) * 1.6).toFixed(2) + 'deg');
    item.style.setProperty('--ucmpc-atm-dr3', ((rand() - .5) * 1.2).toFixed(2) + 'deg');

    item.style.setProperty('--ucmpc-atm-duration', (23 + rand() * 18).toFixed(1) + 's');
    item.style.setProperty('--ucmpc-atm-delay', (-rand() * 16).toFixed(1) + 's');
  }

  function opacityFor(item, rand) {
    if (item.classList.contains(ITEM_CLASS + '--word')) {
      return 0.055 + rand() * 0.025;
    }

    if (item.classList.contains(ITEM_CLASS + '--glyph')) {
      return 0.075 + rand() * 0.035;
    }

    if (item.classList.contains(ITEM_CLASS + '--number')) {
      return 0.24 + rand() * 0.08;
    }

    if (item.classList.contains(ITEM_CLASS + '--mark')) {
      return 0.16 + rand() * 0.06;
    }

    return 0.11 + rand() * 0.05;
  }

  function layoutAtmosphere(editorial, pageType, atmosphere) {
    const viewportWidth = window.innerWidth;

    if (viewportWidth < MIN_WIDTH) {
      atmosphere.hidden = true;
      return;
    }

    const main = document.querySelector('#MainContent');
    if (!main) {
      atmosphere.hidden = true;
      return;
    }

    const mainRect = main.getBoundingClientRect();
    const mainTop = mainRect.top + window.scrollY;
    const mainBottom = mainTop + Math.max(main.scrollHeight, mainRect.height);

    const docHeight = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight
    );

    atmosphere.hidden = false;
    atmosphere.style.setProperty('--ucmpc-atmosphere-height', docHeight + 'px');

    /*
     * El panel central ya está aprobado. La atmósfera solo ocupa
     * los dos corredores laterales, con aire contra panel y viewport.
     */
    const outer = clamp(viewportWidth * 0.035, 42, 68);
    const panelGap = clamp(viewportWidth * 0.022, 26, 42);
    const driftSafety = 22;

    const leftBounds = {
      xMin: outer,
      xMax: mainRect.left - panelGap - driftSafety,
      yMin: mainTop + 46,
      yMax: Math.max(mainTop + 420, mainBottom - 46)
    };

    const rightBounds = {
      xMin: mainRect.right + panelGap + driftSafety,
      xMax: viewportWidth - outer,
      yMin: mainTop + 46,
      yMax: Math.max(mainTop + 420, mainBottom - 46)
    };

    if (
      leftBounds.xMax - leftBounds.xMin < 76 ||
      rightBounds.xMax - rightBounds.xMin < 76
    ) {
      atmosphere.hidden = true;
      return;
    }

    const items = Array.from(
      atmosphere.querySelectorAll('.' + ITEM_CLASS)
    );

    const rand = randomFactory(CONFIG[pageType].seed);
    const placedLeft = [];
    const placedRight = [];

    /*
     * Orden deliberadamente mezclado. No se distribuye por filas,
     * columnas, zigzag ni alternancia predecible.
     */
    items
      .map(function (item) {
        return { item, key: rand() };
      })
      .sort(function (a, b) {
        return a.key - b.key;
      })
      .forEach(function (entry, index) {
        const item = entry.item;
        const size = itemDimensions(item, rand);

        /* 52/48 con pequeñas rachas naturales, no alternancia. */
        const side = rand() < 0.52 ? 'left' : 'right';
        const bounds = side === 'left' ? leftBounds : rightBounds;
        const placed = side === 'left' ? placedLeft : placedRight;

        const position = pickPosition(
          bounds,
          size,
          placed,
          rand
        );

        placed.push(position);

        item.style.setProperty('--ucmpc-atm-x', position.x.toFixed(1) + 'px');
        item.style.setProperty('--ucmpc-atm-y', position.y.toFixed(1) + 'px');
        item.style.setProperty('--ucmpc-atm-opacity', opacityFor(item, rand).toFixed(3));

        applyMotion(item, rand);

        /* Algunos fragmentos se recortan levemente: lenguaje editorial. */
        if (
          item.classList.contains(ITEM_CLASS + '--word') &&
          index % 2 === 0
        ) {
          const nudge = side === 'left'
            ? -(10 + rand() * 22)
            : (10 + rand() * 22);

          item.style.setProperty(
            '--ucmpc-atm-x',
            (position.x + nudge).toFixed(1) + 'px'
          );
        }
      });
  }

  let resizeTimer = null;

  function initAtmosphere() {
    const editorial = getEditorial();
    const pageType = getType(editorial);

    if (!editorial || !pageType) {
      removeAtmosphere();
      return;
    }

    removeLegacyDecor();

    const atmosphere = ensureAtmosphere(pageType);
    layoutAtmosphere(editorial, pageType, atmosphere);
  }

  function schedule() {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(initAtmosphere, 120);
  }

  function start() {
    requestAnimationFrame(function () {
      requestAnimationFrame(initAtmosphere);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('load', schedule, { once: true });
  window.addEventListener('pageshow', start);
  document.addEventListener('shopify:section:load', start);
})();


/* =========================================================
   7. UCMPC V32 — CUENTOS PARA NIÑOS
   Estructura visual de la landing, sin alterar su contenido.
   ========================================================= */

(function () {
  const PATH = '/pages/cuentos-para-ninos';

  function currentPath() {
    return window.location.pathname.replace(/\/$/, '');
  }

  function ensureAtmosphere() {
    const main = document.querySelector('#MainContent');
    if (!main) return;

    let atmosphere = main.querySelector(':scope > .ucmpc-kids-atmosphere');

    if (!atmosphere) {
      atmosphere = document.createElement('div');
      atmosphere.className = 'ucmpc-kids-atmosphere';
      atmosphere.setAttribute('aria-hidden', 'true');

      /*
       * Trayectorias deliberadamente distintas para evitar una retícula
       * o una dirección repetitiva. Cruzan todo el contenido, no solo
       * los laterales, con una opacidad suficientemente baja para que
       * nunca compitan con la lectura.
       */
      const sparks = [
        { type: 'star',   left: -8, top: 6,  size: 13, x: 76,  y: 14,  d: 25, delay: -7,  a: .13, angle: -10 },
        { type: 'energy', left: 10, top: 11, size: 11, x: 58,  y: -7,  d: 29, delay: -16, a: .11 },
        { type: 'star',   left: 31, top: 14, size: 9,  x: 51,  y: 10,  d: 27, delay: -2,  a: .10, angle: 8 },
        { type: 'energy', left: 66, top: 18, size: 13, x: -42, y: 9,   d: 31, delay: -21, a: .11 },
        { type: 'star',   left: 87, top: 21, size: 12, x: -72, y: 17,  d: 30, delay: -12, a: .12, angle: 14 },
        { type: 'energy', left: 19, top: 27, size: 15, x: 43,  y: 14,  d: 35, delay: -4,  a: .10 },
        { type: 'star',   left: 48, top: 31, size: 10, x: 38,  y: -15, d: 26, delay: -18, a: .11, angle: -7 },
        { type: 'energy', left: 78, top: 36, size: 12, x: -55, y: 8,   d: 32, delay: -9,  a: .12 },
        { type: 'star',   left: -6, top: 41, size: 16, x: 98,  y: -12, d: 33, delay: -22, a: .12, angle: -5 },
        { type: 'energy', left: 26, top: 45, size: 10, x: 63,  y: 11,  d: 28, delay: -14, a: .10 },
        { type: 'star',   left: 58, top: 49, size: 14, x: -48, y: 13,  d: 31, delay: -6,  a: .13, angle: 11 },
        { type: 'energy', left: 91, top: 53, size: 10, x: -68, y: 9,   d: 27, delay: -10, a: .11 },
        { type: 'star',   left: 7,  top: 58, size: 11, x: 71,  y: 12,  d: 29, delay: -17, a: .10, angle: 5 },
        { type: 'energy', left: 39, top: 61, size: 14, x: 46,  y: -14, d: 34, delay: -25, a: .10 },
        { type: 'star',   left: 70, top: 65, size: 9,  x: -57, y: -8,  d: 26, delay: -5,  a: .11, angle: -11 },
        { type: 'energy', left: 5,  top: 69, size: 17, x: 49,  y: -16, d: 37, delay: -28, a: .10 },
        { type: 'star',   left: 82, top: 73, size: 14, x: -72, y: -10, d: 32, delay: -8,  a: .13, angle: 9 },
        { type: 'energy', left: 51, top: 77, size: 11, x: 34,  y: 8,   d: 30, delay: -24, a: .11 },
        { type: 'star',   left: 18, top: 81, size: 10, x: 66,  y: -13, d: 28, delay: -3,  a: .11, angle: -13 },
        { type: 'energy', left: 73, top: 84, size: 13, x: -43, y: -9,  d: 34, delay: -15, a: .10 },
        { type: 'star',   left: -4, top: 88, size: 12, x: 82,  y: -12, d: 30, delay: -20, a: .12, angle: 7 },
        { type: 'energy', left: 35, top: 91, size: 9,  x: 44,  y: -8,  d: 26, delay: -11, a: .10 },
        { type: 'star',   left: 63, top: 94, size: 13, x: -51, y: -13, d: 31, delay: -23, a: .12, angle: -9 },
        { type: 'energy', left: 92, top: 96, size: 12, x: -47, y: -7,  d: 33, delay: -6,  a: .10 }
      ];

      sparks.forEach(function (config, index) {
        const spark = document.createElement('span');
        spark.className = `ucmpc-kids-spark ucmpc-kids-spark--${config.type}`;

        spark.style.setProperty('--ucmpc-kids-left', `${config.left}%`);
        spark.style.setProperty('--ucmpc-kids-top', `${config.top}%`);
        spark.style.setProperty('--ucmpc-kids-size', `${config.size}px`);
        spark.style.setProperty('--ucmpc-kids-travel-x', `${config.x}vw`);
        spark.style.setProperty('--ucmpc-kids-travel-y', `${config.y}vh`);
        spark.style.setProperty('--ucmpc-kids-duration', `${config.d}s`);
        spark.style.setProperty('--ucmpc-kids-delay', `${config.delay}s`);
        spark.style.setProperty('--ucmpc-kids-alpha', String(config.a));
        spark.style.setProperty('--ucmpc-kids-rotate-start', `${-12 + index * 2}deg`);
        spark.style.setProperty('--ucmpc-kids-rotate-mid', `${4 - index}deg`);
        spark.style.setProperty('--ucmpc-kids-rotate-end', `${14 + index}deg`);

        if (config.type === 'star') {
          spark.style.setProperty('--ucmpc-kids-tail-angle', `${config.angle || 0}deg`);
          spark.appendChild(document.createElement('i'));
        }

        atmosphere.appendChild(spark);
      });

      main.prepend(atmosphere);
    }
  }

  function buildLanding() {
    if (currentPath() !== PATH) return;

    const editorial = document.querySelector(
      '.ucmpc-page-cuentos-para-ninos'
    );

    if (!editorial) return;

    if (editorial.dataset.ucmpcKidsLanding === 'true') {
      ensureAtmosphere();
      return;
    }

    const paragraphs = Array.from(
      editorial.querySelectorAll(':scope > p')
    );

    if (paragraphs.length < 5) return;

    editorial.dataset.ucmpcKidsLanding = 'true';

    const [lead, spectrum, manifesto, reading, closing] = paragraphs;

    lead.classList.add('ucmpc-kids-lead');
    spectrum.classList.add('ucmpc-kids-spectrum');
    manifesto.classList.add('ucmpc-kids-manifesto');
    reading.classList.add('ucmpc-kids-reading');
    closing.classList.add('ucmpc-kids-closing');

    if (!editorial.querySelector(':scope > .ucmpc-kids-kicker')) {
      const kicker = document.createElement('div');
      kicker.className = 'ucmpc-kids-kicker';
      kicker.textContent = 'Lectores de 7 a 12 años · Historias digitales';
      lead.before(kicker);
    }

    if (!editorial.querySelector(':scope > .ucmpc-kids-reading-tags')) {
      const tags = document.createElement('div');
      tags.className = 'ucmpc-kids-reading-tags';

      [
        'Lectura autónoma',
        'En familia',
        'Con cuidadores',
        'En el aula'
      ].forEach(function (label) {
        const tag = document.createElement('span');
        tag.className = 'ucmpc-kids-reading-tag';
        tag.textContent = label;
        tags.appendChild(tag);
      });

      reading.after(tags);
    }

    if (!editorial.querySelector(':scope > .ucmpc-kids-status')) {
      const status = document.createElement('span');
      status.className = 'ucmpc-kids-status';
      status.textContent = 'Nuevos cuentos en preparación';
      closing.before(status);
    }

    ensureAtmosphere();
  }

  function init() {
    requestAnimationFrame(function () {
      requestAnimationFrame(buildLanding);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.addEventListener('pageshow', init);
  document.addEventListener('shopify:section:load', init);
})();
